// src/components/settings/ScheduleForm.jsx
import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import { schedulingAPI, settingsAPI } from '../../services/api';
import {
    XMarkIcon,
    ClockIcon,
    Cog6ToothIcon,
    TagIcon,
    ShareIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    CheckIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const FREQUENCY_PRESETS = [
    { value: '0 9 * * *', label: 'Daily at 9 AM', description: 'Every day at 9:00 AM' },
    { value: '0 9 * * 1', label: 'Weekly (Monday 9 AM)', description: 'Every Monday at 9:00 AM' },
    { value: '0 9 * * 1,3,5', label: 'Mon/Wed/Fri at 9 AM', description: 'Monday, Wednesday, Friday at 9:00 AM' },
    { value: '0 9,17 * * 1-5', label: 'Twice daily (weekdays)', description: 'Monday-Friday at 9 AM and 5 PM' },
    { value: '0 9 1 * *', label: 'Monthly (1st at 9 AM)', description: 'First day of every month at 9:00 AM' },
    { value: 'custom', label: 'Custom Schedule', description: 'Enter your own cron expression' }
];

const TIMEZONE_OPTIONS = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' }
];

const CONTENT_TYPES = [
    { value: 'article', label: 'Articles' },
    { value: 'blog', label: 'Blog Posts' },
    { value: 'news', label: 'News' },
    { value: 'tutorial', label: 'Tutorials' },
    { value: 'case-study', label: 'Case Studies' }
];

const IMAGE_STYLES = [
    { value: 'professional', label: 'Professional', description: 'Clean, business-focused imagery' },
    { value: 'creative', label: 'Creative', description: 'Artistic and imaginative visuals' },
    { value: 'minimalist', label: 'Minimalist', description: 'Simple, clean designs' },
    { value: 'bold', label: 'Bold', description: 'Strong, attention-grabbing visuals' }
];

const TONE_OPTIONS = [
    { value: 'professional', label: 'Professional', description: 'Formal business tone' },
    { value: 'casual', label: 'Casual', description: 'Relaxed, conversational tone' },
    { value: 'friendly', label: 'Friendly', description: 'Warm and approachable tone' },
    { value: 'authoritative', label: 'Authoritative', description: 'Expert, confident tone' }
];

function ScheduleForm({ schedule, onClose, onSuccess }) {
    const isEditing = !!schedule;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        frequency: '0 9 * * *',
        customCron: '',
        timezone: 'UTC',
        platforms: [],
        keywords: '',
        contentTypes: [],
        imageStyle: 'professional',
        tone: 'professional',
        useFreshContentOnly: true,
        maxPostsPerDay: 1,
        maxPostsPerWeek: 7
    });

    const [errors, setErrors] = useState({});
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Fetch available platforms
    const { data: availablePlatforms } = useQuery(
        'availablePlatforms',
        () => settingsAPI.getPlatforms(),
        {
            onSuccess: (data) => {
                const enabledPlatforms = Object.keys(data.data?.data?.platforms || {})
                    .filter(p => data.data.data.platforms[p]?.enabled);

                if (!isEditing && enabledPlatforms.length > 0) {
                    setFormData(prev => ({ ...prev, platforms: enabledPlatforms }));
                }
            }
        }
    );

    // Fetch content sources
    const { data: contentSources } = useQuery('contentSources', settingsAPI.getContentSources);

    // Fetch plan limits
    const { data: planLimits } = useQuery('planLimits', schedulingAPI.getPlanLimits);

    // Create/update mutation
    const saveMutation = useMutation(
        (data) => isEditing
            ? schedulingAPI.updateSchedule(schedule._id, data)
            : schedulingAPI.createSchedule(data),
        {
            onSuccess: () => {
                toast.success(isEditing ? 'Schedule updated!' : 'Schedule created!');
                onSuccess();
            },
            onError: (error) => {
                const errorMessage = error.response?.data?.message || 'Failed to save schedule';
                toast.error(errorMessage);

                if (error.response?.data?.errors) {
                    setErrors(error.response.data.errors);
                }
            }
        }
    );

    // Load existing schedule data
    useEffect(() => {
        if (schedule) {
            setFormData({
                name: schedule.name || '',
                description: schedule.description || '',
                frequency: schedule.schedule.cronExpression || '0 9 * * *',
                customCron: '',
                timezone: schedule.schedule.timezone || 'UTC',
                platforms: schedule.generationSettings.platforms || [],
                keywords: schedule.contentPreferences.keywords?.join(', ') || '',
                contentTypes: schedule.contentPreferences.contentTypes || [],
                imageStyle: schedule.generationSettings.imageStyle || 'professional',
                tone: schedule.generationSettings.tone || 'professional',
                useFreshContentOnly: schedule.contentPreferences.useFreshContentOnly ?? true,
                maxPostsPerDay: schedule.limits.maxPostsPerDay || 1,
                maxPostsPerWeek: schedule.limits.maxPostsPerWeek || 7
            });

            // Check if it's a custom cron expression
            const isCustom = !FREQUENCY_PRESETS.some(preset => preset.value === schedule.schedule.cronExpression);
            if (isCustom) {
                setFormData(prev => ({
                    ...prev,
                    frequency: 'custom',
                    customCron: schedule.schedule.cronExpression
                }));
            }
        }
    }, [schedule]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear field error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handlePlatformToggle = (platform) => {
        setFormData(prev => ({
            ...prev,
            platforms: prev.platforms.includes(platform)
                ? prev.platforms.filter(p => p !== platform)
                : [...prev.platforms, platform]
        }));
    };

    const handleContentTypeToggle = (type) => {
        setFormData(prev => ({
            ...prev,
            contentTypes: prev.contentTypes.includes(type)
                ? prev.contentTypes.filter(t => t !== type)
                : [...prev.contentTypes, type]
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Schedule name is required';
        }

        if (formData.platforms.length === 0) {
            newErrors.platforms = 'At least one platform must be selected';
        }

        const cronExpression = formData.frequency === 'custom' ? formData.customCron : formData.frequency;
        if (!cronExpression.trim()) {
            newErrors.frequency = 'Schedule frequency is required';
        }

        // Validate cron expression format (basic check)
        if (cronExpression && cronExpression !== 'custom') {
            const cronParts = cronExpression.trim().split(' ');
            if (cronParts.length !== 5) {
                newErrors.frequency = 'Invalid cron expression format';
            }
        }

        // Validate limits against plan
        const limits = planLimits?.data?.data || {};
        if (formData.maxPostsPerDay > (limits.maxPostsPerDay || 1)) {
            newErrors.maxPostsPerDay = `Maximum ${limits.maxPostsPerDay} posts per day allowed for your plan`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fix the errors below');
            return;
        }

        const cronExpression = formData.frequency === 'custom' ? formData.customCron : formData.frequency;
        const frequencyPreset = FREQUENCY_PRESETS.find(p => p.value === cronExpression);

        const scheduleData = {
            name: formData.name.trim(),
            description: formData.description.trim(),
            schedule: {
                cronExpression,
                description: frequencyPreset?.label || 'Custom schedule',
                timezone: formData.timezone
            },
            contentPreferences: {
                keywords: formData.keywords ? formData.keywords.split(',').map(k => k.trim()).filter(k => k) : [],
                contentTypes: formData.contentTypes,
                useFreshContentOnly: formData.useFreshContentOnly
            },
            generationSettings: {
                platforms: formData.platforms,
                imageStyle: formData.imageStyle,
                tone: formData.tone
            },
            limits: {
                maxPostsPerDay: formData.maxPostsPerDay,
                maxPostsPerWeek: formData.maxPostsPerWeek
            }
        };

        saveMutation.mutate(scheduleData);
    };

    const availablePlatformsList = Object.keys(availablePlatforms?.data?.data?.platforms || {})
        .filter(p => availablePlatforms.data.data.platforms[p]?.enabled);

    const limits = planLimits?.data?.data || {};

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    onClick={onClose}
                ></div>

                {/* Modal panel */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                    {/* Header */}
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                    {isEditing ? 'Edit Schedule' : 'Create New Schedule'}
                                </h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {isEditing ? 'Update your automated posting schedule' : 'Set up automated post generation for your social media'}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="bg-white rounded-md text-gray-400 hover:text-gray-500"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="max-h-96 overflow-y-auto">
                        <div className="bg-white px-4 py-5 sm:p-6 space-y-6">
                            {/* Basic Information */}
                            <div className="space-y-4">
                                <h4 className="text-md font-medium text-gray-900 flex items-center">
                                    <InformationCircleIcon className="h-5 w-5 mr-2" />
                                    Basic Information
                                </h4>

                                <div className="grid grid-cols-1 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Schedule Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                            className={`mt-1 block w-full border rounded-md px-3 py-2 ${
                                                errors.name ? 'border-red-300' : 'border-gray-300'
                                            } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                                            placeholder="e.g., Daily Morning Posts"
                                        />
                                        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Description
                                        </label>
                                        <textarea
                                            value={formData.description}
                                            onChange={(e) => handleInputChange('description', e.target.value)}
                                            rows={2}
                                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Describe what this schedule is for..."
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Timing Configuration */}
                            <div className="space-y-4">
                                <h4 className="text-md font-medium text-gray-900 flex items-center">
                                    <ClockIcon className="h-5 w-5 mr-2" />
                                    Timing Configuration
                                </h4>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Frequency *
                                        </label>
                                        <select
                                            value={formData.frequency}
                                            onChange={(e) => handleInputChange('frequency', e.target.value)}
                                            className={`mt-1 block w-full border rounded-md px-3 py-2 ${
                                                errors.frequency ? 'border-red-300' : 'border-gray-300'
                                            } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                                        >
                                            {FREQUENCY_PRESETS.map((preset) => (
                                                <option key={preset.value} value={preset.value}>
                                                    {preset.label}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.frequency && <p className="mt-1 text-sm text-red-600">{errors.frequency}</p>}

                                        <p className="mt-1 text-xs text-gray-500">
                                            {FREQUENCY_PRESETS.find(p => p.value === formData.frequency)?.description}
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Timezone
                                        </label>
                                        <select
                                            value={formData.timezone}
                                            onChange={(e) => handleInputChange('timezone', e.target.value)}
                                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            {TIMEZONE_OPTIONS.map((tz) => (
                                                <option key={tz.value} value={tz.value}>
                                                    {tz.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Custom Cron Expression */}
                                {formData.frequency === 'custom' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Custom Cron Expression *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.customCron}
                                            onChange={(e) => handleInputChange('customCron', e.target.value)}
                                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="0 9 * * * (minute hour day month weekday)"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            Format: minute hour day month weekday (e.g., "0 9 * * *" for daily at 9 AM)
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Platform Selection */}
                            <div className="space-y-4">
                                <h4 className="text-md font-medium text-gray-900 flex items-center">
                                    <ShareIcon className="h-5 w-5 mr-2" />
                                    Target Platforms *
                                </h4>

                                <div className="grid grid-cols-2 gap-3">
                                    {availablePlatformsList.map((platform) => (
                                        <label
                                            key={platform}
                                            className={`flex items-center p-3 rounded-md border cursor-pointer transition-colors ${
                                                formData.platforms.includes(platform)
                                                    ? 'border-blue-300 bg-blue-50'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={formData.platforms.includes(platform)}
                                                onChange={() => handlePlatformToggle(platform)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <span className="ml-3 text-sm font-medium text-gray-900 capitalize">
                                                {platform}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                                {errors.platforms && <p className="text-sm text-red-600">{errors.platforms}</p>}
                            </div>

                            {/* Content Preferences */}
                            <div className="space-y-4">
                                <h4 className="text-md font-medium text-gray-900 flex items-center">
                                    <TagIcon className="h-5 w-5 mr-2" />
                                    Content Preferences
                                </h4>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Keywords (comma-separated)
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.keywords}
                                            onChange={(e) => handleInputChange('keywords', e.target.value)}
                                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="e.g., technology, business, innovation"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            Optional: Focus on content containing these keywords
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Preferred Content Types
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {CONTENT_TYPES.map((type) => (
                                                <button
                                                    key={type.value}
                                                    type="button"
                                                    onClick={() => handleContentTypeToggle(type.value)}
                                                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm border ${
                                                        formData.contentTypes.includes(type.value)
                                                            ? 'bg-blue-100 text-blue-800 border-blue-300'
                                                            : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {formData.contentTypes.includes(type.value) && (
                                                        <CheckIcon className="h-3 w-3 mr-1" />
                                                    )}
                                                    {type.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="useFreshContentOnly"
                                            checked={formData.useFreshContentOnly}
                                            onChange={(e) => handleInputChange('useFreshContentOnly', e.target.checked)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor="useFreshContentOnly" className="ml-2 text-sm text-gray-700">
                                            Use only fresh content (not recently used)
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Generation Settings */}
                            <div className="space-y-4">
                                <h4 className="text-md font-medium text-gray-900 flex items-center">
                                    <Cog6ToothIcon className="h-5 w-5 mr-2" />
                                    Generation Settings
                                </h4>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Image Style
                                        </label>
                                        <select
                                            value={formData.imageStyle}
                                            onChange={(e) => handleInputChange('imageStyle', e.target.value)}
                                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            {IMAGE_STYLES.map((style) => (
                                                <option key={style.value} value={style.value}>
                                                    {style.label}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="mt-1 text-xs text-gray-500">
                                            {IMAGE_STYLES.find(s => s.value === formData.imageStyle)?.description}
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Content Tone
                                        </label>
                                        <select
                                            value={formData.tone}
                                            onChange={(e) => handleInputChange('tone', e.target.value)}
                                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            {TONE_OPTIONS.map((tone) => (
                                                <option key={tone.value} value={tone.value}>
                                                    {tone.label}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="mt-1 text-xs text-gray-500">
                                            {TONE_OPTIONS.find(t => t.value === formData.tone)?.description}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Advanced Settings */}
                            <div className="space-y-4">
                                <button
                                    type="button"
                                    onClick={() => setShowAdvanced(!showAdvanced)}
                                    className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                                >
                                    <Cog6ToothIcon className="h-4 w-4 mr-1" />
                                    {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
                                </button>

                                {showAdvanced && (
                                    <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Max Posts Per Day
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max={limits.maxPostsPerDay || 1}
                                                    value={formData.maxPostsPerDay}
                                                    onChange={(e) => handleInputChange('maxPostsPerDay', parseInt(e.target.value))}
                                                    className={`mt-1 block w-full border rounded-md px-3 py-2 ${
                                                        errors.maxPostsPerDay ? 'border-red-300' : 'border-gray-300'
                                                    } focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                                                />
                                                {errors.maxPostsPerDay && (
                                                    <p className="mt-1 text-sm text-red-600">{errors.maxPostsPerDay}</p>
                                                )}
                                                <p className="mt-1 text-xs text-gray-500">
                                                    Plan limit: {limits.maxPostsPerDay || 1} per day
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Max Posts Per Week
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max={limits.maxPostsPerWeek || 7}
                                                    value={formData.maxPostsPerWeek}
                                                    onChange={(e) => handleInputChange('maxPostsPerWeek', parseInt(e.target.value))}
                                                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                />
                                                <p className="mt-1 text-xs text-gray-500">
                                                    Plan limit: {limits.maxPostsPerWeek || 7} per week
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button
                                type="submit"
                                disabled={saveMutation.isLoading}
                                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                            >
                                {saveMutation.isLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        {isEditing ? 'Updating...' : 'Creating...'}
                                    </>
                                ) : (
                                    <>
                                        <CheckIcon className="h-4 w-4 mr-2" />
                                        {isEditing ? 'Update Schedule' : 'Create Schedule'}
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default ScheduleForm;